import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { drawLine, drawTableRow, drawTextCenter } from './draw-fns';
import { qrcode } from '@akamfoad/qr';
import { drawEnBoiteBanner, renderTableKitchenReceipt } from './tableKitchenCanvas';

// Helper function to wrap text for thermal printer (576px width)
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  if (words.length === 0) return lines;

  let currentLine = words[0];
  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine + ' ' + word;
    const width = ctx.measureText(testLine).width;
    if (width < maxWidth) {
      currentLine = testLine;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
}

type UberKitchenSlip = {
  title?: string;
  orderNumber?: string;
  customerNumber?: string;
  customerName?: string;
};

function uberKitchenSlip(order: Order): UberKitchenSlip | null {
  const slip = (order as Order & { uberSlip?: UberKitchenSlip }).uberSlip;
  if (!slip || typeof slip !== 'object') return null;
  return slip;
}

function orderSpecialInstruction(order: object): string {
  const record = order as { special_instructions?: unknown; uberSlip?: { special_instructions?: unknown } };
  const text = typeof record.special_instructions === "string"
    ? record.special_instructions
    : record.uberSlip?.special_instructions;
  return typeof text === "string" ? text.trim() : "";
}

function dishSpecialInstruction(item: object): string {
  const text = (item as { special_instructions?: unknown }).special_instructions;
  return typeof text === "string" ? text.trim() : "";
}

function drawInstructionLines(
  ctx: CanvasRenderingContext2D,
  y: number,
  text: string,
  fontSize: number,
  indent: number,
  bold = false
): number {
  if (!text) return y;
  ctx.font = `${bold ? "bold" : "normal"} ${fontSize}px Arial`;
  ctx.textAlign = "left";
  wrapText(ctx, text, 536 - indent).forEach((line) => {
    ctx.fillText(line, 10 + indent, y);
    y += fontSize + 4;
  });
  return y;
}

function drawWrappedLine(ctx: CanvasRenderingContext2D, text: string, y: number, font: string, maxWidth: number): number {
  ctx.font = font;
  ctx.textAlign = 'left';
  const size = Number(font.match(/(\d+)px/)?.[1] || 32);
  wrapText(ctx, text, maxWidth).forEach((line) => {
    ctx.fillText(line, 10, y);
    y += size + 6;
  });
  return y;
}

function drawUberKitchenHeader(
  ctx: CanvasRenderingContext2D,
  y: number,
  slip: UberKitchenSlip,
  fontSizeFactor: number,
  store: Store
): number {
  const zone = store?.timeZone || 'America/Toronto';
  const width = ctx.canvas.width || 576;
  const numberSize = Math.round(40 * fontSizeFactor);
  const markSize = Math.round(22 * fontSizeFactor);
  const detailSize = Math.round(28 * fontSizeFactor);
  const orderNumber = String(slip.orderNumber || '').trim();
  const title = String(slip.title || 'Uber').trim();
  ctx.textBaseline = 'alphabetic';
  if (orderNumber) {
    ctx.font = `bold ${numberSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.fillText(orderNumber, width / 2, y);
  }
  ctx.font = `normal ${markSize}px Arial`;
  ctx.textAlign = 'right';
  ctx.fillText(title, width - 10, y);
  y += Math.max(numberSize, markSize) + 8;
  const left = String(slip.customerName || '').trim();
  let when = '';
  try {
    when = format(utcToZonedTime(new Date(), zone), 'HH:mm M/d');
  } catch {
    when = format(new Date(), 'HH:mm M/d');
  }
  ctx.font = `normal ${detailSize}px Arial`;
  ctx.textAlign = 'left';
  if (left) ctx.fillText(left, 10, y);
  ctx.textAlign = 'right';
  ctx.fillText(when, width - 10, y);
  ctx.textAlign = 'left';
  return y + detailSize + 10;
}

// Type definitions
interface OrderItem {
  id: string;
  name: string;
  printName?: string;
  price: number;
  amount: number;
  category?: string;
  itemNote?: string;
  selectedOption?: {
    name: string;
  };
  advancedOptions?: {
    selections?: Array<{
      optionSet: {
        displayName: string;
        options: Array<{
          name: string;
          price: number | string;
          printName: string;
        }>;
      };
      selections: {
        [key: string]: {
          price: number;
          total: number;
          userQuantity: number;
        };
      };
    }>;
    totalPrice?: number;
  };
}

interface Store {
  name: string;
  address?: string;
  email?: string;
  phone?: string;
  timeZone: string;
  message_top?: string;
  message_bottom?: string;
  message_custom?: string;
  currency?: string;
}

interface Order {
  id?: string;
  tableName: string;
  counter?: string;
  items: OrderItem[];
  // Legacy Flat Fields (Deprecated but supported)
  total?: number;
  totalWithTaxes?: number;
  taxes?: Array<{
    taxName: string;
    taxValue: number;
    taxAmount: number;
  }>;
  // StandardOrder Nested Structure (Preferred)
  financials?: {
    subtotal: number;
    taxTotal: number;
    grandTotal: number;
    taxes: Array<{
      taxName: string;
      taxValue: number;
      taxAmount: number;
    }>;
    feeTotal?: number;
    discountTotal?: number;
    adjustmentTotal?: number; // Tip
    serviceFee?: {
      name: string;
      amount: number;
    };
    fees?: Array<{
      name: string;
      amount: number;
      value: number; // percentage or fixed
      type: 'fee';
    }>;
    discounts?: Array<{
      name: string;
      amount: number;
      value: number;
      type: 'discount';
    }>;
  };
  serviceFee?: {
    name: string;
    amount: number;
  };
  feesAndDiscounts?: {
    [key: string]: {
      type: 'fee' | 'discount';
      value: number;
      amount: number;
    };
  };
  guestCount?: {
    total: number;
  };
  customFields?: any;
}

interface Session {
  id: string;
  tableName: string;
  created: any; // Timestamp or Date
  guests?: {
    adult: number;
    kid: number;
    senior: number;
    other: number;
  };
}

// Helper functions for advanced options
const getAdvancedOptionsTotal = (item: OrderItem): number => {
  if (!item.advancedOptions?.selections) return 0;
  return item.advancedOptions.selections.reduce((total, optionGroup) => {
    const groupTotal = Object.values(optionGroup.selections).reduce(
      (sum, opt) => sum + (opt.userQuantity > 0 ? opt.total : 0),
      0
    );
    return total + groupTotal;
  }, 0);
};

const getAdvancedOptionsText = (item: OrderItem, showPrices: boolean = true): string[] => {
  console.log('[optiondebug] getAdvancedOptionsText called for item:', item.name, 'advancedOptions:', item.advancedOptions);

  if (!item.advancedOptions?.selections) {
    console.log('[optiondebug] No advanced options selections found');
    return [];
  }

  const selectedOptions: string[] = [];
  item.advancedOptions.selections.forEach((optionGroup, index) => {
    console.log('[optiondebug] Processing option group', index, ':', optionGroup);

    // Safety check: optionSet might not be included in saved data
    if (!optionGroup.optionSet?.options) {
      console.warn('[optiondebug] Warning: optionSet.options is missing for option group', index, '- using fallback');
    }

    const optionsMap = optionGroup.optionSet?.options
      ? new Map(optionGroup.optionSet.options.map((opt) => [opt.name, opt]))
      : new Map();

    Object.entries(optionGroup.selections || {}).forEach(([name, opt]) => {
      if (opt.userQuantity > 0) {
        const optionDef = optionsMap.get(name);
        // Use printName from optionSet if available, otherwise use the name
        const displayName = (optionDef?.printName || '').trim() || name;

        let optionText = '';
        if (opt.userQuantity >= 1) {
          optionText = `${opt.userQuantity} X ${displayName}`;
        } else {
          optionText = displayName;
        }
        if (showPrices && opt.price > 0) {
          optionText += ` $${opt.total.toFixed(2)}`;
        }
        console.log('[optiondebug] Added option text:', optionText);
        selectedOptions.push(optionText);
      }
    });
  });

  console.log('[optiondebug] Total selected options:', selectedOptions.length, selectedOptions);
  return selectedOptions;
};

/**
 * Render cart items receipt (kitchen slip style)
 */
export const renderCartItemsReceipt = (
  items: OrderItem[],
  store: Store
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  canvas.width = 576;
  canvas.height = 10000;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  let y = 32;

  // Header
  if (store.message_top) {
    y += drawTextCenter(ctx, y, 28, store.message_top, true, false);
  }
  y += drawTextCenter(ctx, y, 28, store.name);
  if (store.address) {
    y += drawTextCenter(ctx, y, 22, store.address);
  }
  if (store.phone) {
    y += drawTextCenter(ctx, y, 22, store.phone);
  }
  // Collect all item notes and display at top
  const itemNotes: string[] = [];
  items.forEach((item) => {
    if (item.itemNote && typeof (item as OrderItem & { special_instructions?: string }).special_instructions !== "string" && !itemNotes.includes(item.itemNote)) {
      itemNotes.push(item.itemNote);
    }
  });

  if (itemNotes.length > 0) {
    const noteFontSize = 32; // Same as dish name
    ctx.font = `normal ${noteFontSize}px Arial`;
    const maxWidth = 536; // 576 - 40 (margins)

    itemNotes.forEach((note) => {
      const wrappedLines = wrapText(ctx, note, maxWidth);
      wrappedLines.forEach((line) => {
        ctx.fillText(line, 10, y);
        y += noteFontSize + 4;
      });
      // Draw dotted line below note
      drawLine(ctx, y - 4);
      y += 8;
    });
  }

  const printTime = format(utcToZonedTime(new Date(), store.timeZone), 'M/d/yyyy, hh:mm a');
  y += drawTextCenter(ctx, y, 28, `Print: ${printTime}`, true, true);

  y += 16;

  // Items
  items.forEach((item) => {
    console.log('[optiondebug] Rendering item for receipt:', item.name, 'itemNote:', item.itemNote);

    drawLine(ctx, y - 16);
    const itemName = item.printName || item.name;

    y += drawTableRow(ctx, y, 32, [item.amount.toString(), itemName, '']);

    // Show advanced options without prices (kitchen style)
    const selectedOptions = getAdvancedOptionsText(item, false);
    selectedOptions.forEach((optionText) => {
      y += drawTableRow(ctx, y, 28, ['', `  • ${optionText}`, '']);
    });
    y = drawInstructionLines(ctx, y, dishSpecialInstruction(item), 28, 10);
  });

  y += 32;
  const data = ctx.getImageData(0, 0, 576, y);
  canvas.height = y - 16;
  ctx.putImageData(data, 0, 0);
  return canvas;
};

/**
 * Render order receipt (customer receipt)
 */
export const renderOrderReceipt = (order: Order, store: Store): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  canvas.width = 576;
  canvas.height = 10000;
  ctx.translate(-10, 0);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  let y = 32;

  // Header section
  if (store.message_top) {
    y += drawTextCenter(ctx, y, 28, store.message_top, true, false);
  }
  y += drawTextCenter(ctx, y, 28, store.name);
  if (store.email) {
    y += drawTextCenter(ctx, y, 28, store.email);
  }
  if (store.address) {
    y += drawTextCenter(ctx, y, 28, store.address);
  }
  // Collect all item notes and display at top
  const itemNotes: string[] = [];
  order.items.forEach((item) => {
    if (item.itemNote && typeof (item as OrderItem & { special_instructions?: string }).special_instructions !== "string" && !itemNotes.includes(item.itemNote)) {
      itemNotes.push(item.itemNote);
    }
  });

  if (itemNotes.length > 0) {
    const noteFontSize = 28; // Same as dish name in renderOrderReceipt
    ctx.font = `normal ${noteFontSize}px Arial`;
    const maxWidth = 536; // 576 - 40 (margins)

    itemNotes.forEach((note) => {
      const wrappedLines = wrapText(ctx, note, maxWidth);
      wrappedLines.forEach((line) => {
        ctx.fillText(line, 10, y);
        y += noteFontSize + 4;
      });
      // Draw dotted line below note
      drawLine(ctx, y - 4);
      y += 8;
    });
  }

  const printTime = format(utcToZonedTime(new Date(), store.timeZone), 'M/d/yyyy, hh:mm a');
  y += drawTextCenter(ctx, y, 28, `Print: ${printTime}`, true, true);

  const guestCountText = order.guestCount?.total
    ? `Table: ${order.tableName}   [#${order.guestCount.total}]    ID: ${order.counter || ''}`
    : `Table: ${order.tableName}    ID: ${order.counter || ''}`;
  y += drawTextCenter(ctx, y, 28, guestCountText, true, false);
  y = drawInstructionLines(ctx, y, orderSpecialInstruction(order), 28, 0, true);

  // Items with advanced options
  order.items.forEach((item) => {
    console.log('[optiondebug] Rendering item for order receipt:', item.name, 'itemNote:', item.itemNote);

    const advancedOptionsTotal = getAdvancedOptionsTotal(item);
    const itemTotalPrice = item.price + advancedOptionsTotal;

    const itemName = item.printName || item.name;

    y += drawTableRow(ctx, y, 28, [
      item.amount.toString(),
      itemName,
      `$${itemTotalPrice.toFixed(2)}   `
    ]);

    // Show advanced options with prices
    const selectedOptions = getAdvancedOptionsText(item, true);
    selectedOptions.forEach((optionText) => {
      y += drawTableRow(ctx, y, 24, ['', `  • ${optionText}`, '']);
    });
    y = drawInstructionLines(ctx, y, dishSpecialInstruction(item), 24, 10);
  });

  drawLine(ctx, y - 32);

  // Totals section (Refactored to nested financials)

  // 1. Subtotal
  const subtotal = order.financials?.subtotal ?? order.total ?? 0;
  y += drawTableRow(ctx, y, 28, ['Sub-Total', '', `$${subtotal.toFixed(2)}   `]);

  // 2. Taxes
  const taxes = order.financials?.taxes ?? order.taxes ?? [];
  if (Array.isArray(taxes)) {
    taxes
      .filter((t) => t.taxAmount)
      .forEach((tax) => {
        y += drawTableRow(ctx, y, 28, [
          `${tax.taxName}@${tax.taxValue}%`,
          '',
          `$${tax.taxAmount.toFixed(2)}   `
        ]);
      });
  }

  // 3. Service Fee (Nested or Legacy)
  const serviceFee = order.financials?.serviceFee ?? order.serviceFee;
  if (serviceFee && serviceFee.name && serviceFee.amount) {
    y += drawTableRow(ctx, y, 28, [
      `${serviceFee.name}`,
      '',
      `$${serviceFee.amount.toFixed(2)}   `
    ]);
  }

  // 4. Other Fees & Discounts
  if (order.financials?.fees || order.financials?.discounts) {
    // Logic for new array-based fees/discounts if populated
    order.financials.fees?.forEach(fee => {
      y += drawTableRow(ctx, y, 28, [`${fee.name}`, '', `$${fee.amount.toFixed(2)}   `]);
    });
    order.financials.discounts?.forEach(discount => {
      y += drawTableRow(ctx, y, 28, [`${discount.name}`, '', `-$${discount.amount.toFixed(2)}   `]);
    });
  } else if (order.feesAndDiscounts) {
    // Legacy Map-based
    Object.entries(order.feesAndDiscounts).forEach(([name, item]) => {
      const amount = item.type === 'discount' ? -item.amount : item.amount;
      const sign = item.type === 'discount' ? '-' : '';

      y += drawTableRow(ctx, y, 28, [
        `${name} (${item.value}%)`,
        '',
        `${sign}$${Math.abs(amount).toFixed(2)}   `
      ]);
    });
  }

  // 5. Grand Total
  const grandTotal = order.financials?.grandTotal ?? order.totalWithTaxes ?? 0;
  y += drawTableRow(ctx, y, 36, ['Grand Total', '', `$${grandTotal.toFixed(2)}   `]);
  drawLine(ctx, y - 36);

  if (order.customFields) {
    ctx.font = 'normal 28px Arial';
    Object.values(order.customFields)
      .sort((a: any, b: any) => a.fieldData.displayOrder - b.fieldData.displayOrder)
      .forEach((field: any) => {
        const value = Array.isArray(field.inputValue)
          ? field.inputValue.join(', ')
          : field.inputValue;

        ctx.fillStyle = '#000000';
        ctx.fillRect(10, y - 30, 30, 35);
        ctx.fillStyle = '#ffffff';
        ctx.fillText(field.fieldData.displayOrder.toString(), 18, y);
        ctx.fillStyle = '#000000';
        ctx.fillText(`: ${value}`, 50, y);
        y += 40;
      });
    drawLine(ctx, y - 36);
  }

  if (store.message_custom) {
    y += 32 + drawTextCenter(ctx, y, 32, store.message_custom);
  }
  y += 32;

  const data = ctx.getImageData(0, 0, 576, y);
  canvas.height = y - 16;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.putImageData(data, 0, 0);
  return canvas;
};

/**
 * Render kitchen receipt
 */
export const renderKitchenReceipt = (
  order: Order,
  store: Store,
  printer?: any
): HTMLCanvasElement => {
  if ((order as any)?.isTableOrder) {
    return renderTableKitchenReceipt(order, store, printer, 'standard') as HTMLCanvasElement;
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return canvas;

  const font_increase_percentage = printer?.font_increase_percentage || 0;
  const empty_lines_on_top = printer?.empty_lines_on_top || 0;
  const fontSizeFactor = 1 + font_increase_percentage / 100;

  canvas.width = 576;
  canvas.height = 10000;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';

  let y = 32 + empty_lines_on_top * 16;

  if (order.customFields) {
    const customFieldFontSize = Math.round(28 * fontSizeFactor);
    ctx.font = `normal ${customFieldFontSize}px Arial`;
    Object.values(order.customFields)
      .sort((a: any, b: any) => a.fieldData.displayOrder - b.fieldData.displayOrder)
      .forEach((field: any) => {
        const value = Array.isArray(field.inputValue)
          ? field.inputValue.join(', ')
          : field.inputValue;

        ctx.fillStyle = '#000000';
        ctx.fillRect(10, y - 25, 25, 25);
        ctx.fillStyle = '#ffffff';
        ctx.fillText(field.fieldData.displayOrder.toString(), 17, y - 5);
        ctx.fillStyle = '#000000';
        y += drawTableRow(ctx, y, customFieldFontSize, [`: ${value}`, '', '']);
      });
    drawLine(ctx, y - 32);
    y += 32;
  }

  // Collect all item notes and display at top
  const itemNotes: string[] = [];
  order.items.forEach((item) => {
    if (item.itemNote && typeof (item as OrderItem & { special_instructions?: string }).special_instructions !== "string" && !itemNotes.includes(item.itemNote)) {
      itemNotes.push(item.itemNote);
    }
  });

  if (itemNotes.length > 0) {
    const noteFontSize = Math.round(32 * fontSizeFactor); // Same as dish name (itemNameFontSize)
    ctx.font = `normal ${noteFontSize}px Arial`;
    const maxWidth = 536; // 576 - 40 (margins)

    itemNotes.forEach((note) => {
      const wrappedLines = wrapText(ctx, note, maxWidth);
      wrappedLines.forEach((line) => {
        ctx.fillText(line, 10, y);
        y += noteFontSize + 4;
      });
      // Draw dotted line below note
      drawLine(ctx, y - 4);
      y += 8;
    });
  }

  const uberSlip = uberKitchenSlip(order);
  if (uberSlip) {
    y = drawUberKitchenHeader(ctx, y, uberSlip, fontSizeFactor, store);
  } else {
    y = drawEnBoiteBanner(ctx, y, fontSizeFactor);
    const printTimeShort = format(utcToZonedTime(new Date(), store.timeZone), ' HH:mm M/d ');
    const orderCountShort = String(order.counter || '').slice(-3);
    const tableNameFontSize = Math.round(36 * fontSizeFactor);
    const orderInfoFontSize = 36;

    ctx.font = `normal ${tableNameFontSize}px Arial`;
    ctx.fillText(`${order.tableName}`, 10, y);

    ctx.font = `normal ${orderInfoFontSize}px Arial`;
    const tableMeasure = ctx.measureText(`${order.tableName}`);
    ctx.fillText(`         ${orderCountShort}`, tableMeasure.width + 20, y);
    ctx.fillText(`${printTimeShort}    `, tableMeasure.width + 200, y);
  }

  y = drawInstructionLines(ctx, y, orderSpecialInstruction(order), Math.round(28 * fontSizeFactor), 0, true);
  const spacingBetweenHeaderAndItems = Math.round(40 * fontSizeFactor);
  y += spacingBetweenHeaderAndItems;

  order.items.forEach((item) => {
    console.log('[optiondebug] Rendering item for kitchen receipt:', item.name, 'itemNote:', item.itemNote);

    drawLine(ctx, y - 32);
    const itemNameFontSize = Math.round(32 * fontSizeFactor);
    const itemName = item.printName || item.name;

    y += drawTableRow(ctx, y, itemNameFontSize, [item.amount.toString(), itemName, '']);

    const selectedOptions = getAdvancedOptionsText(item, false);
    const optionFontSize = Math.round(28 * fontSizeFactor);
    selectedOptions.forEach((optionText) => {
      y += drawTableRow(ctx, y, optionFontSize, ['', `  • ${optionText}`, '']);
    });
    y = drawInstructionLines(ctx, y, dishSpecialInstruction(item), optionFontSize, 10);
  });

  y += 32;
  const data = ctx.getImageData(0, 0, 576, y);
  canvas.height = y - 16;
  ctx.putImageData(data, 0, 0);
  return canvas;
};

/**
 * Render session receipt (QR code)
 */
export const renderSessionReceipt = (
  session: Session,
  store: Store,
  qrType: 'session_qr' | 'fixed_table_qr' = 'session_qr',
  tables?: Array<{ name: string; url: string }>
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  canvas.width = 576;
  canvas.height = 760;
  ctx.translate(-20, 0);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  let y = 32;

  if (store.message_top) {
    y += drawTextCenter(ctx, y, 28, store.message_top, true, false);
  }
  y += drawTextCenter(ctx, y, 28, store.name);
  if (store.phone) {
    y += drawTextCenter(ctx, y, 28, store.phone);
  }
  if (store.address) {
    y += drawTextCenter(ctx, y, 22, store.address);
  }
  const printTime = format(utcToZonedTime(new Date(), store.timeZone), 'M/d/yyyy, hh:mm a');
  y += drawTextCenter(ctx, y, 28, `Print: ${printTime}`, true, true);

  y += drawTextCenter(ctx, y, 36, `Table: ${session.tableName}`);

  const { adult = 0, kid = 0, senior = 0, other = 0 } = session.guests || {};

  const createTime = session.created?.toDate
    ? format(utcToZonedTime(session.created.toDate(), store.timeZone), 'M/d/yyyy, hh:mm a')
    : format(utcToZonedTime(new Date(session.created), store.timeZone), 'M/d/yyyy, hh:mm a');

  y += drawTextCenter(ctx, y, 28, `${adult}A${kid}K${senior}S${other}O`);

  let url: string = '';
  if (qrType === 'session_qr') {
    // Need store URL - this would come from store data
    url = `${window.location.origin}/order?s=${session.id}`;
  } else if (qrType === 'fixed_table_qr') {
    const table = tables?.find((t) => t.name === session.tableName);
    url = table?.url || `${session.tableName}: Fixed Table URL is not set!`;
  }

  const cells = qrcode(url);

  let x = 0;
  y -= 24;
  if (cells.modules) {
    for (let i = 0; i < cells.modules.length; i++) {
      y += 6;
      x = 180;
      for (let j = 0; j < cells.modules.length; j++) {
        if (cells.modules[i][j]) {
          ctx.fillRect(x, y, 6, 6);
        }
        x += 6;
      }
    }
  }
  y += 60;

  y += 16 + drawTextCenter(ctx, y, 28, createTime, true, true);
  if (store.message_bottom) {
    y += 16 + drawTextCenter(ctx, y, 32, store.message_bottom);
  }
  if (store.message_custom) {
    y += 16 + drawTextCenter(ctx, y, 28, store.message_custom);
  }
  y += 36;

  const data = ctx.getImageData(0, 0, 576, y);
  canvas.height = y - 16;
  ctx.putImageData(data, 0, 0);
  return canvas;
};

/**
 * Render kitchen receipt cut by dish (one receipt per item)
 * Reused from silent-printer's renderKitchenReceiptCutByDish
 */
export const renderKitchenReceiptCutByDish = (
  order: Order,
  store: Store,
  printer?: any
): HTMLCanvasElement[] => {
  if ((order as any)?.isTableOrder) {
    return renderTableKitchenReceipt(order, store, printer, 'item') as HTMLCanvasElement[];
  }
  const canvases: HTMLCanvasElement[] = [];

  // Get font increase percentage and empty lines settings from printer if available
  const font_increase_percentage = printer?.font_increase_percentage || 0;
  const empty_lines_on_top = printer?.empty_lines_on_top || 0;
  const fontSizeFactor = 1 + font_increase_percentage / 100;

  order.items.forEach((item) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    canvas.width = 576;
    canvas.height = 10000;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';

    // Add empty lines at the top (each line is 16px in height)
    let y = 32 + empty_lines_on_top * 16;

    if (order.customFields) {
      // Apply font size increase for custom fields (28px)
      const customFieldFontSize = Math.round(28 * fontSizeFactor);
      ctx.font = `normal ${customFieldFontSize}px Arial`;
      Object.values(order.customFields)
        .sort((a: any, b: any) => a.fieldData.displayOrder - b.fieldData.displayOrder)
        .forEach((field: any) => {
          const value = Array.isArray(field.inputValue)
            ? field.inputValue.join(', ')
            : field.inputValue;

          ctx.fillStyle = '#000000';
          ctx.fillRect(10, y - 30, 30, 35);
          ctx.fillStyle = '#ffffff';
          ctx.fillText(field.fieldData.displayOrder.toString(), 18, y);
          ctx.fillStyle = '#000000';
          ctx.fillText(`: ${value}`, 50, y);
          y += 40;
        });
      drawLine(ctx, y - 36);
      y += 40;
    }

    // Display item note at top if present. A dish instruction prints under that dish.
    if (item.itemNote && typeof (item as OrderItem & { special_instructions?: string }).special_instructions !== "string") {
      const noteFontSize = Math.round(34 * fontSizeFactor); // Same as dish name (itemNameFontSize)
      ctx.font = `normal ${noteFontSize}px Arial`;
      const maxWidth = 536; // 576 - 40 (margins)

      const wrappedLines = wrapText(ctx, item.itemNote, maxWidth);
      wrappedLines.forEach((line) => {
        ctx.fillText(line, 10, y);
        y += noteFontSize + 4;
      });
      // Draw dotted line below note
      drawLine(ctx, y - 4);
      y += 8;
    }

    const uberSlip = uberKitchenSlip(order);
    if (uberSlip) {
      y = drawUberKitchenHeader(ctx, y, uberSlip, fontSizeFactor, store);
    } else {
      y = drawEnBoiteBanner(ctx, y, fontSizeFactor);
      const printTimeShort = format(utcToZonedTime(new Date(), store.timeZone), ' HH:mm M/d ');
      const orderCountShort = String(order.counter || '').slice(-3);
      drawLine(ctx, y - 32);

      // Only increase font size for table name, keep others at original size
      const tableNameFontSize = Math.round(36 * fontSizeFactor);
      const orderInfoFontSize = 36; // Keep original size for order count and time

      // Custom drawing to handle different font sizes
      ctx.font = `normal ${tableNameFontSize}px Arial`;
      ctx.fillText(`${order.tableName}`, 10, y);

      // Use original font size for order count and time
      ctx.font = `normal ${orderInfoFontSize}px Arial`;
      const tableMeasure = ctx.measureText(`${order.tableName}`);
      ctx.fillText(`         ${orderCountShort}`, tableMeasure.width + 20, y);
      const guestCountTotal = order.guestCount?.total || 0;
      ctx.fillText(`${printTimeShort}[#${guestCountTotal}]    `, tableMeasure.width + 200, y);
    }

    y = drawInstructionLines(ctx, y, orderSpecialInstruction(order), Math.round(28 * fontSizeFactor), 0, true);
    // Scale the spacing between table name and items based on font size increase
    const spacingBetweenHeaderAndItems = Math.round(40 * fontSizeFactor);
    y += spacingBetweenHeaderAndItems;
    drawLine(ctx, y - 32);

    // Apply font size increase for item name (34px)
    const itemNameFontSize = Math.round(34 * fontSizeFactor);
    // Scale the negative offset based on font size increase
    const itemOffset = Math.round(-10 * fontSizeFactor);

    const itemName = item.printName || item.name;

    y += itemOffset + drawTableRow(ctx, y, itemNameFontSize, [item.amount.toString(), itemName, '']);

    // Show advanced options without prices
    const selectedOptions = getAdvancedOptionsText(item, false);
    // Apply font size increase for options (30px)
    const optionFontSize = Math.round(30 * fontSizeFactor);
    selectedOptions.forEach((optionText) => {
      y += drawTableRow(ctx, y, optionFontSize, ['', `  • ${optionText}`, '']);
    });
    y = drawInstructionLines(ctx, y, dishSpecialInstruction(item), optionFontSize, 10);

    const data = ctx.getImageData(0, 0, 576, y);
    canvas.height = y - 16;
    ctx.putImageData(data, 0, 0);
    canvases.push(canvas);
  });

  return canvases;
};

/**
 * Render kitchen receipt cut by category (one receipt per category)
 * Reused from silent-printer's renderKitchenReceiptCutByCategory
 */
export const renderKitchenReceiptCutByCategory = (
  order: Order,
  store: Store,
  printer?: any
): HTMLCanvasElement[] => {
  if ((order as any)?.isTableOrder) {
    return renderTableKitchenReceipt(order, store, printer, 'category') as HTMLCanvasElement[];
  }
  const canvases: HTMLCanvasElement[] = [];

  // Get font increase percentage and empty lines settings from printer if available
  const font_increase_percentage = printer?.font_increase_percentage || 0;
  const empty_lines_on_top = printer?.empty_lines_on_top || 0;
  const fontSizeFactor = 1 + font_increase_percentage / 100;

  const itemsByCategory: Record<string, OrderItem[]> = {};
  order.items.forEach((item) => {
    const category = item.category || 'uncategorized';
    if (!itemsByCategory[category]) {
      itemsByCategory[category] = [];
    }
    itemsByCategory[category].push(item);
  });

  Object.entries(itemsByCategory).forEach(([category, items]) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    canvas.width = 576;
    canvas.height = 10000;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';

    // Add empty lines at the top (each line is 16px in height)
    let y = 75 + empty_lines_on_top * 16;

    if (order.customFields) {
      // Apply font size increase for custom fields (36px)
      const customFieldFontSize = Math.round(36 * fontSizeFactor);
      ctx.font = `normal ${customFieldFontSize}px Arial`;
      Object.values(order.customFields)
        .sort((a: any, b: any) => a.fieldData.displayOrder - b.fieldData.displayOrder)
        .forEach((field: any) => {
          const value = Array.isArray(field.inputValue)
            ? field.inputValue.join(', ')
            : field.inputValue;

          ctx.fillText(`${field.fieldData.displayOrder}: ${value}`, 20, y);
          y += 40;
        });
      drawLine(ctx, y - 32);
      drawLine(ctx, y - 36);
      y += 40;
    }

    // Collect all item notes and display at top
    const itemNotes: string[] = [];
    items.forEach((item) => {
      if (item.itemNote && typeof (item as OrderItem & { special_instructions?: string }).special_instructions !== "string" && !itemNotes.includes(item.itemNote)) {
        itemNotes.push(item.itemNote);
      }
    });

    if (itemNotes.length > 0) {
      const noteFontSize = Math.round(36 * fontSizeFactor); // Same as dish name in this function
      ctx.font = `normal ${noteFontSize}px Arial`;
      const maxWidth = 536; // 576 - 40 (margins)

      itemNotes.forEach((note) => {
        const wrappedLines = wrapText(ctx, note, maxWidth);
        wrappedLines.forEach((line) => {
          ctx.fillText(line, 10, y);
          y += noteFontSize + 4;
        });
        // Draw dotted line below note
        drawLine(ctx, y - 4);
        y += 8;
      });
    }

    const uberSlip = uberKitchenSlip(order);
    if (uberSlip) {
      y = drawUberKitchenHeader(ctx, y, uberSlip, fontSizeFactor, store);
    } else {
      y = drawEnBoiteBanner(ctx, y, fontSizeFactor);
      const printTimeShort = format(utcToZonedTime(new Date(), store.timeZone), ' HH:mm M/d ');
      const orderCountShort = String(order.counter || '').slice(-3);

      // Only increase font size for table name, keep order info at original size
      const tableNameFontSize = Math.round(57 * fontSizeFactor);
      ctx.font = `bold ${tableNameFontSize}px Arial`;
      const tableName = `${order.tableName}`;
      ctx.fillText(`${tableName}`, 10, y);

      // Keep original font size for order info (38px)
      const orderInfoFontSize = 38; // Original size without scaling
      ctx.font = ` ${orderInfoFontSize}px Arial`;
      const guestCountTotal = order.guestCount?.total || 0;
      ctx.fillText(
        `     [#${guestCountTotal}]         ${orderCountShort}    ${printTimeShort}`,
        ctx.measureText(`${tableName}`).width + 20,
        y
      );
    }

    y = drawInstructionLines(ctx, y, orderSpecialInstruction(order), Math.round(28 * fontSizeFactor), 0, true);

    // Scale the spacing between table name and category based on font size increase
    const spacingBetweenHeaderAndCategory = Math.round(80 * fontSizeFactor);
    y += spacingBetweenHeaderAndCategory;

    // Apply font size increase for category (36px)
    const categoryFontSize = Math.round(36 * fontSizeFactor);
    ctx.font = `normal ${categoryFontSize}px Arial`;
    // Use the scaled font size (28px) for the category text
    const categoryTextFontSize = Math.round(28 * fontSizeFactor);
    y += drawTextCenter(ctx, y, categoryTextFontSize, ` ${category}`);
    drawLine(ctx, y - 36);

    items.forEach((item) => {
      const itemName = item.printName || item.name;

      y += drawTableRow(ctx, y, 36, [item.amount.toString(), itemName, '']);

      // Show advanced options without prices
      const selectedOptions = getAdvancedOptionsText(item, false);
      selectedOptions.forEach((optionText) => {
        y += drawTableRow(ctx, y, 32, ['', `  • ${optionText}`, '']);
      });
      y = drawInstructionLines(ctx, y, dishSpecialInstruction(item), 28, 10);
      drawLine(ctx, y - 36);
    });

    const data = ctx.getImageData(0, 0, 576, y);
    canvas.height = y - 16;
    ctx.putImageData(data, 0, 0);
    canvases.push(canvas);
  });

  return canvases;
};

/**
 * Render kitchen receipt with price
 * Reused from silent-printer's renderKitchenReceiptWithPrice
 */
export const renderKitchenReceiptWithPrice = (
  order: Order,
  store: Store,
  printer?: any
): HTMLCanvasElement => {
  if ((order as any)?.isTableOrder) {
    return renderTableKitchenReceipt(order, store, printer, 'price') as HTMLCanvasElement;
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  canvas.width = 576;
  canvas.height = 10000;
  ctx.translate(-10, 0);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  let y = 32;

  // Header section
  y += drawTextCenter(ctx, y, 28, store.message_top || '', true, false);
  y += drawTextCenter(ctx, y, 28, store.name);
  if (store.email) {
    y += drawTextCenter(ctx, y, 28, store.email);
  }
  if (store.address) {
    y += drawTextCenter(ctx, y, 28, store.address);
  }
  // Collect all item notes and display at top (before print time and counter)
  const itemNotes: string[] = [];
  order.items.forEach((item) => {
    if (item.itemNote && typeof (item as OrderItem & { special_instructions?: string }).special_instructions !== "string" && !itemNotes.includes(item.itemNote)) {
      itemNotes.push(item.itemNote);
    }
  });

  if (itemNotes.length > 0) {
    const noteFontSize = 28; // Same as dish name in renderKitchenReceiptWithPrice
    ctx.font = `normal ${noteFontSize}px Arial`;
    const maxWidth = 536; // 576 - 40 (margins)

    itemNotes.forEach((note) => {
      const wrappedLines = wrapText(ctx, note, maxWidth);
      wrappedLines.forEach((line) => {
        ctx.fillText(line, 10, y);
        y += noteFontSize + 4;
      });
      // Draw dotted line below note
      drawLine(ctx, y - 4);
      y += 8;
    });
  }

  const printTime = format(utcToZonedTime(new Date(), store.timeZone), 'M/d/yyyy, hh:mm a');
  y += drawTextCenter(ctx, y, 28, `Print: ${printTime}`, true, true);

  const uberSlip = uberKitchenSlip(order);
  if (uberSlip) {
    y = drawUberKitchenHeader(ctx, y, uberSlip, 1, store);
  } else {
    y = drawEnBoiteBanner(ctx, y, 1);
    const guestCountTotal = order.guestCount?.total || 0;
    y += drawTextCenter(
      ctx,
      y,
      28,
      `Table: ${order.tableName}   [#${guestCountTotal}]    ID: ${order.counter || ''}`,
      true,
      false
    );
  }

  // Calculate the subtotal ourselves by adding up all items
  let calculatedSubtotal = 0;

  y = drawInstructionLines(ctx, y, orderSpecialInstruction(order), 28, 0, true);

  // Items with advanced options
  order.items.forEach((item) => {
    console.log('[optiondebug] Rendering item for kitchen receipt with price:', item.name, 'itemNote:', item.itemNote);

    const advancedOptionsTotal = getAdvancedOptionsTotal(item);
    const itemTotalPrice = item.price + advancedOptionsTotal;

    const itemName = item.printName || item.name;

    // Add to our running subtotal
    calculatedSubtotal += itemTotalPrice * item.amount;

    y += drawTableRow(ctx, y, 28, [
      item.amount.toString(),
      itemName,
      `$${itemTotalPrice.toFixed(2)}   `
    ]);

    // Show advanced options with prices
    const selectedOptions = getAdvancedOptionsText(item, true);
    selectedOptions.forEach((optionText) => {
      y += drawTableRow(ctx, y, 24, ['', `  • ${optionText}`, '']);
    });
    y = drawInstructionLines(ctx, y, dishSpecialInstruction(item), 24, 10);
  });

  drawLine(ctx, y - 32);

  // Use our calculated subtotal if order.total is 0
  const subtotalToShow = order.total > 0 ? order.total : calculatedSubtotal;

  // Totals section - Show subtotal only, no taxes or service fees
  y += drawTableRow(ctx, y, 28, ['Sub-Total', '', `$${subtotalToShow.toFixed(2)}   `]);

  y += 32 + drawTextCenter(ctx, y, 32, store.message_custom || '');
  y += 32;

  const data = ctx.getImageData(0, 0, 576, y);
  canvas.height = y - 16;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.putImageData(data, 0, 0);
  return canvas;
};

/**
 * Sales Report Data Interface
 */
interface SalesReportData {
  totalSales: number;
  orderCount: number;
  totalTaxes?: number;
  totalServiceFees?: number;
  paymentBreakdown?: {
    [method: string]: {
      count: number;
      amount: number;
    };
  };
  employeeSales?: {
    [employee: string]: {
      count: number;
      sales: number;
    };
  };
}

/**
 * Render POS Sales Report for 3-inch thermal printer
 */
export const renderPOSSalesReport = (
  reportData: SalesReportData,
  store: Store,
  stationStartTime: Date,
  endTime: Date
): HTMLCanvasElement => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  canvas.width = 576;
  canvas.height = 10000;
  ctx.translate(-10, 0);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#000000';
  let y = 32;

  // Store header
  y += drawTextCenter(ctx, y, 28, store.message_top || '', true, false);
  y += drawTextCenter(ctx, y, 28, store.name);
  if (store.address) {
    y += drawTextCenter(ctx, y, 24, store.address);
  }
  if (store.phone) {
    y += drawTextCenter(ctx, y, 24, store.phone);
  }

  const printTime = format(utcToZonedTime(new Date(), store.timeZone), 'M/d/yyyy, hh:mm a');
  y += drawTextCenter(ctx, y, 28, `Print: ${printTime}`, true, true);

  // Report title
  y += 16;
  y += drawTextCenter(ctx, y, 32, 'POS SALES REPORT', true, false);
  y += drawTextCenter(ctx, y, 22, '(POS Sales Only)', false, false);
  y += 8;

  // Report period
  const formatDate = (date: Date) => {
    return format(utcToZonedTime(date, store.timeZone), 'M/d/yyyy, hh:mm a');
  };

  y += drawTextCenter(ctx, y, 24, 'Report Period:', false, false);
  y += drawTextCenter(ctx, y, 22, `${formatDate(stationStartTime)}`, false, false);
  y += drawTextCenter(ctx, y, 22, `to ${formatDate(endTime)}`, false, false);
  y += drawTextCenter(ctx, y, 20, `Generated: ${formatDate(new Date())}`, false, false);

  drawLine(ctx, y + 8);
  y += 24;

  // Summary section
  y += drawTextCenter(ctx, y, 26, 'SUMMARY', true, false);
  y += 8;

  const currency = store.currency || 'USD';
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Total Sales
  y += drawTableRow(ctx, y, 26, ['Total Sales', '', `${formatCurrency(reportData.totalSales || 0)}   `]);

  // Total Orders
  y += drawTableRow(ctx, y, 26, ['Total Orders', '', `${reportData.orderCount || 0}   `]);

  // Average Order Value
  const avgOrderValue = reportData.orderCount > 0
    ? (reportData.totalSales || 0) / reportData.orderCount
    : 0;
  y += drawTableRow(ctx, y, 26, ['Avg Order Value', '', `${formatCurrency(avgOrderValue)}   `]);

  // Total Taxes (if applicable)
  if (reportData.totalTaxes && reportData.totalTaxes > 0) {
    y += drawTableRow(ctx, y, 26, ['Total Taxes', '', `${formatCurrency(reportData.totalTaxes)}   `]);
  }

  // Total Service Fees (if applicable)
  if (reportData.totalServiceFees && reportData.totalServiceFees > 0) {
    y += drawTableRow(ctx, y, 26, ['Service Fees', '', `${formatCurrency(reportData.totalServiceFees)}   `]);
  }

  y += 8;
  drawLine(ctx, y);
  y += 16;

  // Payment Method Breakdown
  if (reportData.paymentBreakdown && Object.keys(reportData.paymentBreakdown).length > 0) {
    y += drawTextCenter(ctx, y, 26, 'PAYMENT BREAKDOWN', true, false);
    y += 8;

    // Sort by amount descending
    const paymentEntries = Object.entries(reportData.paymentBreakdown)
      .sort((a, b) => b[1].amount - a[1].amount);

    paymentEntries.forEach(([method, data]) => {
      const percentage = reportData.totalSales > 0
        ? ((data.amount / reportData.totalSales) * 100).toFixed(1)
        : '0.0';

      y += drawTableRow(ctx, y, 24, [method, '', `${formatCurrency(data.amount)}   `]);
      y += drawTableRow(ctx, y, 22, ['', `Count: ${data.count} (${percentage}%)`, '']);
      y += 4;
    });

    y += 8;
    drawLine(ctx, y);
    y += 16;
  }

  // Employee Sales
  if (reportData.employeeSales && Object.keys(reportData.employeeSales).length > 0) {
    y += drawTextCenter(ctx, y, 26, 'EMPLOYEE SALES', true, false);
    y += 8;

    // Sort by sales descending
    const employeeEntries = Object.entries(reportData.employeeSales)
      .sort((a, b) => b[1].sales - a[1].sales);

    employeeEntries.forEach(([employee, data]) => {
      y += drawTableRow(ctx, y, 24, [employee || 'Unknown', '', `${formatCurrency(data.sales)}   `]);
      y += drawTableRow(ctx, y, 22, ['', `Orders: ${data.count}`, '']);
      y += 4;
    });

    y += 8;
    drawLine(ctx, y);
    y += 16;
  }

  // Footer
  if (store.message_custom) {
    y += drawTextCenter(ctx, y, 24, store.message_custom);
    y += 8;
  }
  if (store.message_bottom) {
    y += drawTextCenter(ctx, y, 24, store.message_bottom);
    y += 8;
  }

  y += 16;

  const data = ctx.getImageData(0, 0, 576, y);
  canvas.height = y - 16;
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.putImageData(data, 0, 0);
  return canvas;
};

export { };

